import React, { useEffect, useState, useRef, useMemo, useContext } from "react"
import { useEffectFirstSkip } from "components/actions/customHook/useEffectFirstSkip"
import Modal from "react-bootstrap/Modal"
import ClassNames from "classnames"
import {
  get,
  find,
  toNumber,
  isEmpty,
  map,
  compact,
  split,
  includes,
  pick,
  set,
  cloneDeep,
  merge,
} from "lodash"
import APIs from "redux/apis"

import * as Formatter from "utils/Formatter"
import {
  getPriceCompareText,
  priceCompareIcon,
  getRate,
  checkNoneData,
} from "utils/util"
import MiddleChart from "components/molecules/chart/middleChart"
import OrderBook from "components/organisms/orderBook"
import { MoveSpTab, Tab } from "components/atoms/btn/tab"
import { FavoriteModal } from "./utils"
import PushSymbol from "components/molecules/pushSymbol"
import { GetTicketKey } from "node-dist/nodeDistFlag"
import { changeSymbol } from "redux/ducks/Chart"
import { FOREIGN_STOCK, history } from "config/const"
import uuid from "uuid"
import { Ticker } from "components/molecules/ticker"

import {
  SymbolDetailStateType,
  TabListType,
  useSettledAccountReportSummary,
} from "components/actions/symbolDetail"
import { APIResponse } from "redux/apis/types"
import {
  MARKET_VARIABLES,
  SP_SECTOR_LIST,
  EQUITY_SECURITY_LIST,
  SECURITY_TYPE,
  EXCHANGE_LIST,
} from "config/const"
import { SymbolRegulationModal } from "components/organisms/modals/symbolDetail/symbolRegulation"
import { SymbolDetailContext } from "components/pages/others/symbolDetail"

import { checkIsSp, getCountryIcon } from "utils/util"
import store from "redux/store"
import { showSymbol, refreshSymbol } from "redux/ducks/SymbolDetail"
import { ChartInputNumeric } from "components/atoms/chart/parts/ChartInputNumeric"
import { initRegisterLoad } from "redux/ducks/RegisterSymbol"
import { SearchDetail } from "../../../utils/search"
import { pageLoadRegist } from "redux/ducks/PageLoad"
import { window_open } from "utils/util"
import { MainContainer } from "components/molecules/utils/mainContainer"
import { DataDescription } from "../../panel/dataDescription"
import {
  SymbolDetailDisclaimerText,
  SymbolDataDisclaimerText,
} from "../../../../config/disclaimerText"

export type SymbolDetailModalType = {
  show?: boolean
  symbolDetail?: APIResponse.Res
  symbolDetailState: SymbolDetailStateType
  components?: {
    id: number
    name: string
    component: React.FC<any>
    props: object
  }
  onHide?: Function
  tabList: TabListType
  changeTab: Function
  regulation: Array<APIResponse.Res>
  history: history
  windowMode?: boolean
}

type CalcInvestmentCostProps = {
  show: boolean
  onHide: Function
  Symbol: APIResponse.Res
}
type useCalcInvestmentCostType = (
  symbol: APIResponse.Res,
  reset: boolean,
  rate: number
) => [number, Function, number, Function, number, Function, number]
const useCalcInvestmentCost: useCalcInvestmentCostType = (
  symbol,
  reset,
  rate
) => {
  const defaultPrice = get(symbol, "Price") || get(symbol, "StandardPrice")
  const [price, setPrice] = useState<number>(defaultPrice)
  const [stock, setStock] = useState<number>(get(symbol, "TradingUnit"))
  const [addPrice, setAddPrice] = useState<number>(null)
  const [total, setTotal] = useState<number>(price * rate * stock + addPrice)

  useEffect(() => {
    setTotal(price * rate * stock + addPrice)
  }, [rate, stock, addPrice, price])
  useEffect(() => {
    if (reset) {
      setPrice(defaultPrice)
      setStock(get(symbol, "TradingUnit"))
      setAddPrice(null)
    }
  }, [reset])
  return [price, setPrice, stock, setStock, addPrice, setAddPrice, total]
}
const CalcInvestmentCost: React.FC<CalcInvestmentCostProps> = props => {
  const loadingId = useMemo(() => uuid(), [])
  const [rate, setRate] = useState<number>(1)
  useEffectFirstSkip(() => {
    if (!props.Symbol) return // //API 為替レート呼び出し
    ;(async () => {
      store.dispatch(pageLoadRegist(loadingId, true))
      const res = await APIs.SymbolDetail.PriceList({
        Name: "Top.FX.JPY",
      })
      setRate(getRate(get(res, "ResultList"), props.Symbol.Exchange))
      store.dispatch(pageLoadRegist(loadingId, false))
    })()
  }, [props.Symbol])

  const [
    price,
    setPrice,
    stock,
    setStock,
    addPrice,
    setAddPrice,
    total,
  ] = useCalcInvestmentCost(props.Symbol, props.show, rate)
  const securityType = get(props.Symbol, "SecurityType")

  return (
    <div className={ClassNames("calc-price-container", { show: props.show })}>
      <div className="header-icon ripple" onClick={() => props.onHide()}>
        <i className="material-icons">close</i>
      </div>
      <table className="table table-striped">
        <tbody>
          <tr>
            <th className="title-cell">現値</th>
            <td>
              <ChartInputNumeric
                defaultValue={price || ""}
                step={1}
                min={1}
                max={999999}
                decimalDigits={get(props.Symbol, "NumberOfDecimals")}
                setValue={setPrice}
                manualOnly={true}
              />
            </td>
          </tr>
          <tr>
            <th className="title-cell">為替レート</th>
            <td>
              {securityType !== SECURITY_TYPE.Equity.id ? (
                <ChartInputNumeric
                  defaultValue={rate}
                  step={1}
                  min={1}
                  max={999999}
                  decimalDigits={0}
                  setValue={setRate}
                  manualOnly={true}
                />
              ) : (
                1
              )}
            </td>
          </tr>
          <tr>
            <th className="title-cell">売買単位</th>
            <td>{Formatter.number(get(props.Symbol, "TradingUnit"))}</td>
          </tr>
          <tr>
            <th className="title-cell">数量</th>
            <td>
              <ChartInputNumeric
                defaultValue={stock}
                step={get(props.Symbol, "TradingUnit")}
                min={1}
                max={999999}
                decimalDigits={0}
                setValue={setStock}
                manualOnly={true}
              />
            </td>
          </tr>
          <tr>
            <th className="title-cell">手数料</th>
            <td>
              <ChartInputNumeric
                defaultValue={addPrice || 0}
                step={1}
                min={0}
                max={999999}
                decimalDigits={2}
                setValue={setAddPrice}
                manualOnly={true}
              />
            </td>
          </tr>
          <tr className="investment-">
            <th className="title-cell investment-cell">投資金額</th>
            <td>
              {Formatter.number(total, get(props.Symbol, "NumberOfDecimals"))}
            </td>
          </tr>
        </tbody>
      </table>
      <div
        className="btn btn-primary btn-shaped"
        onClick={() => props.onHide()}
      >
        閉じる
      </div>
    </div>
  )
}

const FavoriteSymbol: React.FC<CalcInvestmentCostProps> = props => {
  return (
    <div className={ClassNames("calc-price-container", { show: props.show })}>
      <div className="header-icon ripple" onClick={() => props.onHide()}>
        <i className="material-icons">close</i>
      </div>
      <FavoriteModal onHide={() => props.onHide()} symbol={props.Symbol} />
      <div
        className="btn btn-primary btn-shaped"
        onClick={() => props.onHide()}
      >
        閉じる
      </div>
    </div>
  )
}

const IndexValuesBtn: React.FC<{
  nikkei: boolean
  tp: boolean
  moveToIndex: Function
}> = props => {
  return props.nikkei || props.tp ? (
    <>
      {props.tp ? <div className="btn btn-outline">TOPIX100</div> : null}
      {props.nikkei ? (
        <div
          className="btn btn-outline"
          onClick={() => {
            props.moveToIndex()
          }}
        >
          日経平均
        </div>
      ) : null}
    </>
  ) : null
}

const LoansMarginsBtn: React.FC<{ loadMargin: number }> = props => {
  return !!props.loadMargin ? (
    <div className="btn btn-outline">
      {props.loadMargin === 1 ? "貸借" : "融資"}
    </div>
  ) : null
}
const indicationSizeValue = {
  size_5: 5,
  size_10: 10,
}

const regulationLabelList = [
  { title: "売却のみ可", param: "NotAvailable" },
  { title: "ネット取引不可", param: "NotNetAvailable" },
  // { title: "即日入金規制", param: "SameDayDeposit" },
  // { title: "安定操作可能期間", param: "StableOperation" },
  // { title: "注意喚起", param: "Attention" },
  // { title: "日々公表", param: "PublishedDaily" },
  // { title: "委託保証金率変更", param: "ChangeMarginRate" },
  // { title: "品資料臨時借置", param: "TemporaryRenting" },
  // { title: "公募・売出し", param: "OfferSale" },
]

export const SymbolDetailModalContext = React.createContext<{
  symbolDetail?: object
  regulation?: object
  displaySymbolRegulationModal?: Function
  onHide?: Function
  history?: any
  symbolDetailState?: SymbolDetailStateType
  components?: object
  tabList?: TabListType
  changeTab?: Function
  canvasId?: string
  show?: boolean
  loadingId?: string
  windowMode?: boolean
}>({})

export const SymbolDetailModal: React.FC<SymbolDetailModalType> = props => {
  const {
    symbolDetail,
    regulation,
    onHide,
    history,
    symbolDetailState,
    components,
    tabList,
    changeTab,
    show,
    windowMode,
  } = props
  const canvasId = useMemo(() => {
    return uuid()
  }, [])
  const loadingId = useMemo(() => uuid(), [])
  const [showSymbolRegulationModal, displaySymbolRegulationModal] = useState(
    false
  )
  const isSp = useMemo(() => {
    return checkIsSp()
  }, [])
  const symbolDetailContext = useContext(SymbolDetailContext)
  const { showPerformanceNewsFlag } = symbolDetailContext
  //スマホで業績のニュースをモーダルで開くと閉じるボタンが効かなくなるので一時的に銘柄検索詳細のモーダルを消すフラグ
  const temporarilyHidden = isSp && showPerformanceNewsFlag ? "none" : "block"
  // ディスクレーマを表示するタブ
  const Disclaimer_TAB = [0, 1, 2, 3]
  let disclaimerFlag = Disclaimer_TAB.includes(symbolDetailState.currentTab)
  // 銘柄詳細ディスクレーマを表示するタブ 2は時系列データ
  const SymbolDetail_Disclaimer_TAB = [0, 1, 3]

  return (
    <SymbolDetailModalContext.Provider
      value={{
        symbolDetail,
        regulation,
        onHide,
        displaySymbolRegulationModal,
        history,
        symbolDetailState,
        components,
        tabList,
        changeTab,
        canvasId,
        show,
        loadingId,
        windowMode,
      }}
    >
      {!windowMode ? (
        <Modal
          show={props.show}
          onHide={() => props.onHide()}
          size="xl"
          className="modal-symbol-detail"
          // paddong-left,displayがインラインに直に書かれているため、上書き。
          style={{ padding: 0, display: temporarilyHidden }}
        >
          <Modal.Header>
            <HeaderContents />
          </Modal.Header>
          {props.show ? (
            <Modal.Body>
              <BodyContents />
            </Modal.Body>
          ) : null}
        </Modal>
      ) : (
        <div
          className={ClassNames("modal-symbol-detail", {
            "is-window": windowMode,
          })}
        >
          <div className="modal-header">
            <HeaderContents />
          </div>
          <MainContainer className="modal-body">
            <BodyContents />
            {!isSp && disclaimerFlag && (
              <DataDescription
                className="panel-data-description"
                children={
                  SymbolDetail_Disclaimer_TAB.includes(
                    symbolDetailState.currentTab
                  )
                    ? SymbolDetailDisclaimerText
                    : SymbolDataDisclaimerText
                }
              />
            )}
          </MainContainer>
        </div>
      )}
      <SymbolRegulationModal
        show={showSymbolRegulationModal}
        onHide={() => displaySymbolRegulationModal(false)}
        regulationData={props.regulation}
      />
    </SymbolDetailModalContext.Provider>
  )
}

// SymbolDetail Header部分
const HeaderContents = () => {
  const [calcShow, displayCalc] = useState<boolean>(false)
  const [favoriteShow, displayFavorite] = useState<boolean>(false)
  const [focusEffect, setFocusEffect] = useState(false)
  const symbolDetailModalContext = useContext(SymbolDetailModalContext)
  const {
    symbolDetail,
    symbolDetailState,
    onHide,
    canvasId,
    loadingId,
    windowMode,
  } = symbolDetailModalContext
  const [prevTab, setPrevTab] = useState(symbolDetailState.currentTab)
  const [tickerEffect, setTickerEffect] = useState(false)
  const isSp = useMemo(() => {
    return checkIsSp()
  }, [])
  // SymbolDetailから作成するデータ全てを、メモ化。
  // 以下で一括管理。
  const { symbol, symbolName } = useMemo(() => {
    const symbol = get(symbolDetail, "Symbol")
    const symbolName = get(symbol, "SymbolName")

    return {
      symbol,
      symbolName,
    }
  }, [symbolDetail])

  useEffect(() => {
    setFocusEffect(true)
  }, [])

  useEffect(() => {
    displayCalc(false)
    displayFavorite(false)
  }, [symbolDetail])

  useEffect(() => {
    if (prevTab === symbolDetailState.currentTab) return
    setPrevTab(symbolDetailState.currentTab)
  }, [symbolDetailState.currentTab])

  useEffectFirstSkip(() => {
    setTickerEffect(true)
  }, [prevTab])

  const [headerDescription, setHeaderDescription] = useState<string | null>(
    null
  )

  useEffectFirstSkip(() => {
    if (isEmpty(symbol)) return
    ;(async () => {
      store.dispatch(changeSymbol(canvasId, symbol))
      await store.dispatch(pageLoadRegist(loadingId, true))
      const company = await APIs.SymbolDetail.GetCompanyInfo(
        pick(symbol, ["Symbol", "SecurityType"])
      )
      setHeaderDescription(get(company, ["CompanyInfo", "Description"], null))
      await store.dispatch(pageLoadRegist(loadingId, false))
    })()
  }, [symbol])

  return (
    <div className="modal-header-symbol">
      {!isSp ? (
        <>
          <div className="modal-header-symbol-contents">
            <div className="modal-header-symbol-row">
              <SearchDetail
                className="search-bar"
                placeholder="銘柄コード/銘柄名"
                executeFunction={(obj: APIResponse.Res) =>
                  store.dispatch(showSymbol(obj))
                }
                forceActive={true}
                isDirect={true}
                inputId="search-detail-header"
                focusEffect={focusEffect}
                setFocusEffect={setFocusEffect}
              />
              <div className="header-content">
                <Ticker
                  isPush={true}
                  effectItem={{
                    effect: tickerEffect,
                    setEffect: setTickerEffect,
                  }}
                />
              </div>
            </div>
            <div className="modal-header-symbol-row">
              <div className="test-wrap">
                <div className="btn btn-primary">
                  {Formatter.symbol(symbol)}
                </div>
                <div className="modal-header-symbol-name">{symbolName}</div>
              </div>
              {!isSp && !includes(FOREIGN_STOCK, get(symbol, "SecurityType")) && (
                <>
                  <div className="header-description-group">
                    <div className="btn btn-primary header-description-btn">
                      特色
                    </div>
                    <div className="header-description-content">
                      {headerDescription}
                    </div>
                  </div>
                  {headerDescription &&
                    symbolName &&
                    // 特色の文言と銘柄名の長さが、合わせて60（銘柄名はフォント数的に×2）になった時見切れるため下記を表示する
                    headerDescription.length + symbolName.length * 2 > 60 && (
                      <div className="hover-items">{headerDescription}</div>
                    )}
                </>
              )}
              <div className="modal-header-icon-group">
                <div
                  className="header-icon ripple"
                  onClick={() => {
                    store.dispatch(initRegisterLoad())
                    displayCalc(false)
                    displayFavorite(!favoriteShow)
                  }}
                >
                  <i className="material-icons" title={"銘柄リスト登録"}>
                    star
                  </i>
                </div>
                <div
                  className="header-icon ripple"
                  onClick={() => {
                    store.dispatch(
                      showSymbol(
                        get(symbolDetail, "Symbol"),
                        symbolDetailState.currentTab,
                        true
                      )
                    )
                    store.dispatch(refreshSymbol())
                  }}
                >
                  <i className="material-icons" title={"更新"}>
                    sync
                  </i>
                </div>
              </div>
            </div>
            <div className="modal-header-symbol-row">
              <HeaderBtns />
              <div
                className="btn btn-primary btn-shaped calc-investment-cost-btn"
                onClick={() => {
                  displayFavorite(false)
                  displayCalc(!calcShow)
                }}
              >
                投資金額計算
              </div>
            </div>
          </div>
          {!windowMode && (
            <div
              className="header-icon ripple close-btn"
              onClick={() => onHide()}
            >
              <i className="material-icons">close</i>
            </div>
          )}
          <CalcInvestmentCost
            show={calcShow}
            onHide={() => displayCalc(false)}
            Symbol={symbol}
          />
          <FavoriteSymbol
            show={favoriteShow}
            onHide={() => displayFavorite(false)}
            Symbol={symbol}
          />
        </>
      ) : (
        <div className="modal-header-symbol-contents isSp">
          <SpHeader />
        </div>
      )}
    </div>
  )
}
// SymbolDetail Body部分
const BodyContents = () => {
  const symbolDetailModalContext = useContext(SymbolDetailModalContext)
  const {
    symbolDetail,
    symbolDetailState,
    components,
    tabList,
    changeTab,
    canvasId,
    show,
  } = symbolDetailModalContext
  const isSp = useMemo(() => {
    return checkIsSp()
  }, [])
  const [indicationSize, changeIndicationSize] = useState<number>(
    indicationSizeValue.size_5
  )
  const [isReverseChartAndBoard, reverseChartAndBoard] = useState(false)
  const [apiSymbolData, setApiSymbolData] = useState({})
  const panelBody = useRef<HTMLDivElement>(null)

  const { symbol } = useMemo(() => {
    const symbol = get(symbolDetail, "Symbol")
    return {
      symbol,
    }
  }, [symbolDetail])

  const [settledSummary] = useSettledAccountReportSummary(symbol, show)
  //PER(予)、PBR(実)、配当利回(予)のデータ(settledSummary)が表示されないことがあるのでapiSymbolに追加
  //renderIfChangedに追記すると各タブのrenderが走り2度APIをコールしてしまう
  useEffect(() => {
    if (isEmpty(symbol)) return
    const _symbol = cloneDeep(symbol)
    merge(_symbol, settledSummary)
    setApiSymbolData(_symbol)
  }, [settledSummary])

  // Pushを受ける度レンダリングがはしるのでmemo化。
  // タブ以下のconents。
  const component = useMemo(() => {
    return React.createElement(get(components, "component"), {
      ...get(components, "props"),
    })
  }, [get(components, "component"), get(components, "props")])

  return (
    <PushSymbol
      apiSymbol={apiSymbolData}
      ticketKey={GetTicketKey(symbol)}
      renderingId={get(components, "id")}
      renderIfChanged={{
        apiSymbolData,
        indicationSize,
        changeIndicationSize,
        isReverseChartAndBoard,
        reverseChartAndBoard,
      }}
      renderer={(
        push: any,
        apiSymbol: any,
        dataPush: any,
        renderIfChanged: any
      ) => {
        return (
          <div className="panel panel-symbol-detail" {...dataPush}>
            <PanelSymbolInfo
              pushSymbol={push}
              apiSymbol={apiSymbol}
              renderingData={get(renderIfChanged, "apiSymbolData")}
            />
            <div className="panel-body" ref={panelBody}>
              <div
                className={ClassNames("panel-splits", {
                  "panel-splits-content": !isSp,
                })}
              >
                {!isSp ? (
                  <Tab
                    list={tabList}
                    active={symbolDetailState.currentTab}
                    onChange={changeTab}
                    className="foot-border-tabs nav-tabs"
                  />
                ) : (
                  <MoveSpTab
                    list={tabList}
                    active={symbolDetailState.currentTab}
                    onChange={changeTab}
                    className="foot-border-tabs nav-tabs"
                    container={panelBody.current}
                  />
                )}
                {component}
              </div>
              {!isSp ? (
                <div
                  className={ClassNames(
                    "panel-splits panel-split-price-col right-contents",
                    { reverse: isReverseChartAndBoard }
                  )}
                >
                  <MiddleChart id={canvasId} notShowPriceDetail={true} />
                  <div className="board-order-header">
                    <OrderBook
                      push={push}
                      Symbol={(symbol as any) || {}}
                      length={
                        get(apiSymbol, "SecurityType") ===
                        SECURITY_TYPE.Equity.id
                          ? renderIfChanged.indicationSize
                          : 5
                      }
                    />
                    <div className="indication-size-btn-group">
                      {get(apiSymbol, "SecurityType") ===
                        SECURITY_TYPE.Equity.id && (
                        <>
                          <div
                            className="btn btn-primary icon-change"
                            onClick={() =>
                              renderIfChanged.reverseChartAndBoard(
                                !renderIfChanged.isReverseChartAndBoard
                              )
                            }
                            title={"レイアウト変更"}
                          >
                            <i className="material-icons">swap_vert</i>
                          </div>
                          <div
                            className={ClassNames(
                              "btn btn-primary btn-shaped indication-size-btn",
                              {
                                deactivate:
                                  renderIfChanged.indicationSize !==
                                  indicationSizeValue.size_5,
                              }
                            )}
                            onClick={() => {
                              if (
                                renderIfChanged.indicationSize ===
                                indicationSizeValue.size_5
                              )
                                return
                              renderIfChanged.changeIndicationSize(
                                indicationSizeValue.size_5
                              )
                            }}
                          >
                            5本表示
                          </div>
                          <div
                            className={ClassNames(
                              "btn btn-primary btn-shaped indication-size-btn",
                              {
                                deactivate:
                                  renderIfChanged.indicationSize !==
                                  indicationSizeValue.size_10,
                              }
                            )}
                            onClick={() => {
                              if (
                                renderIfChanged.indicationSize ===
                                indicationSizeValue.size_10
                              )
                                return
                              renderIfChanged.changeIndicationSize(
                                indicationSizeValue.size_10
                              )
                            }}
                          >
                            10本表示
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )
      }}
    />
  )
}
// SymbolDetail Body内 Header部分
const PanelSymbolInfo = (props: {
  pushSymbol?: object
  apiSymbol: object
  renderingData: object
}) => {
  const { apiSymbol, pushSymbol, renderingData } = props
  const [priceCompareText, setPriceCompareText] = useState("")
  const isSp = useMemo(() => {
    return checkIsSp()
  }, [])
  const priceCompare =
    get(pushSymbol, "PriceCompare") || get(apiSymbol, "PriceCompare")
  const iconClass = priceCompareIcon(priceCompare)

  useEffect(() => {
    priceCompare
      ? getPriceCompareText(priceCompare, priceCompareText, setPriceCompareText)
      : setPriceCompareText("")
  }, [pushSymbol, apiSymbol])

  const {
    Price,
    NumberOfDecimals,
    ChangedRatio,
    ChangedPrice,
    PriceTime,
    OpenPrice,
    OpenPriceTime,
    HighPrice,
    HighPriceTime,
    LowPrice,
    LowPriceTime,
    LastClose,
    LastCloseTime,
    TradVolume,
    R_HaitouRimawari,
    R_PBR,
    R_PER,
  } = useMemo(() => {
    const Price = !isEmpty(pushSymbol)
      ? get(pushSymbol, "Price")
      : get(apiSymbol, "Price")
    const ChangedRatio = !isEmpty(pushSymbol)
      ? get(pushSymbol, "ChangedRatio")
      : get(apiSymbol, "ChangedRatio")
    const ChangedPrice = !isEmpty(pushSymbol)
      ? get(pushSymbol, "ChangedPrice")
      : get(apiSymbol, "ChangedPrice")
    const PriceTime = !isEmpty(pushSymbol)
      ? get(pushSymbol, "PriceTime")
      : get(apiSymbol, "PriceTime")
    const OpenPrice = !isEmpty(pushSymbol)
      ? get(pushSymbol, "OpenPrice")
      : get(apiSymbol, "OpenPrice")
    const OpenPriceTime = !isEmpty(pushSymbol)
      ? get(pushSymbol, "OpenPriceTime")
      : get(apiSymbol, "OpenPriceTime")
    const HighPrice = !isEmpty(pushSymbol)
      ? get(pushSymbol, "HighPrice")
      : get(apiSymbol, "HighPrice")
    const HighPriceTime = !isEmpty(pushSymbol)
      ? get(pushSymbol, "HighPriceTime")
      : get(apiSymbol, "HighPriceTime")
    const LowPrice = !isEmpty(pushSymbol)
      ? get(pushSymbol, "LowPrice")
      : get(apiSymbol, "LowPrice")
    const LowPriceTime = !isEmpty(pushSymbol)
      ? get(pushSymbol, "LowPriceTime")
      : get(apiSymbol, "LowPriceTime")
    const LastClose = !isEmpty(pushSymbol)
      ? get(pushSymbol, "CalcLastClose") || get(pushSymbol, "LastClose")
      : get(apiSymbol, "CalcLastClose") || get(apiSymbol, "LastClose")
    const LastCloseTime = !isEmpty(pushSymbol)
      ? get(pushSymbol, "LastCloseTime")
      : get(apiSymbol, "LastCloseTime")
    const TradVolume = !isEmpty(pushSymbol)
      ? get(pushSymbol, "TradVolume")
      : get(apiSymbol, "TradVolume")
    const NumberOfDecimals = get(apiSymbol, "NumberOfDecimals")
    const R_HaitouRimawari = get(apiSymbol, "R_HaitouRimawari")
    const R_PBR = get(apiSymbol, "R_PBR")
    const R_PER = get(apiSymbol, "R_PER")
    return {
      Price,
      NumberOfDecimals,
      ChangedRatio,
      ChangedPrice,
      PriceTime,
      OpenPrice,
      OpenPriceTime,
      HighPrice,
      HighPriceTime,
      LowPrice,
      LowPriceTime,
      LastClose,
      LastCloseTime,
      TradVolume,
      R_HaitouRimawari,
      R_PBR,
      R_PER,
    }
  }, [pushSymbol, apiSymbol, renderingData])

  return (
    <div className="panel-symbol-info">
      <div className="panel-symbol-info-row">
        <div className="current-price-group">
          <div className="current-price-item">
            <span className="current-price">
              {Formatter.number(Price, NumberOfDecimals)}
            </span>
            <i
              className={ClassNames("material-icons", iconClass, {
                empty: isEmpty(priceCompareText),
              })}
            >
              {priceCompareText}
            </i>
          </div>
          <div className="current-price-item">
            <span className="time-col">
              {Formatter.date(PriceTime, "HH:mm")}
            </span>
          </div>
        </div>
        {!isSp && (
          <div className="symbol-info-content">
            <ul>
              <li className="symbol-info-table-column">
                <div>始</div>
                <div className="panel-symbol-info-number">
                  {Formatter.number(OpenPrice, NumberOfDecimals)}
                  <span className="time-col">
                    {Formatter.date(OpenPriceTime, "HH:mm")}
                  </span>
                </div>
              </li>
              <li className="symbol-info-table-column">
                <div>高</div>
                <div className="panel-symbol-info-number">
                  {Formatter.number(HighPrice, NumberOfDecimals)}
                  <span className="time-col">
                    {Formatter.date(HighPriceTime, "HH:mm")}
                  </span>
                </div>
              </li>
              <li className="performance-table-column">
                <div className="performance-table-title">PER(予)</div>
                <div className="performance-table-contents">
                  <div>{Formatter.number(R_PER, 2)}</div>
                  <div className="unit">倍</div>
                </div>
              </li>
              <li className="symbol-info-table-column">
                <div>出来高</div>
                <div className="panel-symbol-info-number">
                  {Formatter.number(TradVolume)}
                  <span className="unit unit-s">株</span>
                </div>
              </li>
            </ul>
          </div>
        )}
      </div>
      {!isSp && (
        <div className="panel-symbol-info-row">
          <div className="current-price-group">
            <div className="current-price-item">
              <span className={ClassNames(Formatter.upDown(ChangedRatio))}>
                {Formatter.number(
                  ChangedPrice,
                  NumberOfDecimals,
                  "floor",
                  true
                )}
              </span>
              (
              <span className={ClassNames(Formatter.upDown(ChangedRatio))}>
                {Formatter.number(ChangedRatio, 2, "floor", true)}
              </span>
              <span className="unit unit-s">%</span>)
            </div>
          </div>
          <div className="symbol-info-content">
            <ul>
              <li className="symbol-info-table-column">
                <div>終</div>
                <div className="panel-symbol-info-number">
                  {Formatter.number(LastClose, NumberOfDecimals)}
                  <span className="time-col">
                    {Formatter.date(LastCloseTime, "MM/DD")}
                  </span>
                </div>
              </li>
              <li className="symbol-info-table-column">
                <div>安</div>
                <div className="panel-symbol-info-number">
                  {Formatter.number(LowPrice, NumberOfDecimals)}
                  <span className="time-col">
                    {Formatter.date(LowPriceTime, "HH:mm")}
                  </span>
                </div>
              </li>
              <li className="performance-table-column">
                <div className="performance-table-title">PBR(実)</div>
                <div className="performance-table-contents">
                  <div>{Formatter.number(R_PBR, 2)}</div>
                  <div className="unit">倍</div>
                </div>
              </li>
              <li className="performance-table-column">
                <div className="performance-table-title">配当利回(予)</div>
                <div className="performance-table-contents">
                  <div>{Formatter.number(R_HaitouRimawari, 2)}</div>
                  <div className="unit">%</div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

// SymbolDetail Body内 Header部分
const HeaderBtns = () => {
  const symbolDetailModalContext = useContext(SymbolDetailModalContext)
  const symbolDetailContext = useContext(SymbolDetailContext)
  const { setFlag, currentTab } = symbolDetailContext

  const {
    symbolDetail,
    regulation,
    displaySymbolRegulationModal,
    onHide,
    history,
    symbolDetailState,
    windowMode,
  } = symbolDetailModalContext
  const [hoverExchange, changeHoverExchange] = useState<number>(null)

  // SymbolDetailから作成するデータ全てを、メモ化。
  // 以下で一括管理。
  const {
    symbol,
    exchangeDivisions,
    industryName,
    spIndustryName,
    loanMargin,
    hasNikkeiIndex,
    hasTPIndex,
  } = useMemo(() => {
    const symbol = get(symbolDetail, "Symbol")
    const exchangeDivisions = get(symbol, "ExchangeDivisions")
    const industryName = find(MARKET_VARIABLES.INDUSTRY, {
      value: get(symbol, "JSecCode"),
    })
    const spIndustryName = find(SP_SECTOR_LIST, {
      SPSecCode: get(symbol, "SPSecCode"),
    })
    const loanMargin = get(symbol, "LoansMargins")
    const indexValues = split(get(symbol, "IndexValues"), ",")
    const hasNikkeiIndex = includes(indexValues, "NK225")
    const hasTPIndex = includes(indexValues, "TP100")

    return {
      symbol,
      exchangeDivisions,
      industryName,
      spIndustryName,
      loanMargin,
      hasNikkeiIndex,
      hasTPIndex,
    }
  }, [symbolDetail])
  const isPhone = useMemo(() => {
    return checkIsSp()
  }, [])

  const regulationLabels = compact(
    map(regulationLabelList, (data: { title: string; param: string }) => {
      return get(regulation, `[0].${data.param}`) === 1 ? data.title : null
    })
  )

  return (
    <div
      className={ClassNames("modal-header-symbol-item", {
        isSp: isPhone,
      })}
    >
      {includes(FOREIGN_STOCK, get(symbol, "SecurityType")) && (
        <div className="btn btn-primary btn-star-id">
          {get(symbol, "StarID")}
        </div>
      )}
      {!isEmpty(regulationLabels) &&
        map(regulationLabels, (regulationTitle: string) => {
          return <div className="btn btn-outline">{regulationTitle}</div>
        })}
      {get(symbol, "SecurityType") === SECURITY_TYPE.Equity.id ? (
        exchangeDivisions.map(
          (
            li: {
              Exchange: number
              ExchangeName: string
              Division: string
              DivisionName: string
            },
            i: number
          ) => {
            const isMain = li.Exchange === get(symbol, "MainExchange")
            const isHover = li.Exchange === hoverExchange
            const selectExchange = li.Exchange === get(symbol, "Exchange")
            return (
              <div
                key={i}
                className={ClassNames("btn btn-outline", {
                  active: selectExchange || isHover,
                })}
                onClick={async () => {
                  if (li.Exchange === get(symbolDetail, "Symbol.Exchange"))
                    return
                  if (includes([0, 4], currentTab)) setFlag(0)
                  store.dispatch(
                    showSymbol(
                      {
                        Symbol: get(symbolDetail, "Symbol.Symbol"),
                        SecurityType: get(symbolDetail, "Symbol.SecurityType"),
                        Exchange: li.Exchange,
                      },
                      symbolDetailState.currentTab,
                      true
                    )
                  )
                }}
                onMouseOver={() => {
                  changeHoverExchange(li.Exchange)
                }}
                onMouseLeave={() => changeHoverExchange(null)}
              >
                {`${li.ExchangeName}${li.DivisionName}`}
                {isMain ? "最良" : ""}
              </div>
            )
          }
        )
      ) : (
        <div className="btn btn-outline active">
          {get(symbol, "ExchangeName")}
        </div>
      )}
      {industryName ? (
        <div
          className="btn btn-outline"
          onClick={async () => {
            if (!windowMode) {
              history.push(
                `/domestic/industry?code=${get(
                  industryName,
                  "value"
                )}&symbol=${get(symbolDetail, "Symbol.Symbol")}`
              )
              onHide()
            }

            if (windowMode)
              window_open(
                "./#/domestic/industry",
                {
                  code: get(industryName, "value"),
                  symbol: get(symbolDetail, "Symbol.Symbol"),
                },
                "Main-Page",
                {},
                false
              )
          }}
        >
          {get(industryName, "label")}
        </div>
      ) : null}
      {spIndustryName ? (
        <div
          className="btn btn-outline"
          onClick={() => {
            if (!windowMode) {
              history.push(
                `/tools/search?code=${get(symbolDetail, "Symbol.SPSecCode")}`
              )
              onHide()
            }
            if (windowMode) {
              window_open(
                "./#/tools/search",
                { code: get(symbolDetail, "Symbol.SPSecCode") },
                "Main-Page",
                {},
                false
              )
            }
          }}
        >
          {get(spIndustryName, "name")}
        </div>
      ) : null}
      <LoansMarginsBtn loadMargin={loanMargin} />
      <IndexValuesBtn
        nikkei={hasNikkeiIndex}
        tp={hasTPIndex}
        moveToIndex={() => {
          if (!windowMode) {
            history.push("/top/index")
            onHide()
          }
          if (windowMode) window_open("./#/top/index", {}, "Main-Page")
        }}
      />
      {!isEmpty(regulation) &&
        get(symbol, "SecurityType") === SECURITY_TYPE.Equity.id && (
          <div
            onClick={() => displaySymbolRegulationModal(true)}
            className="regulation-btn"
          >
            <i className="material-icons regulation">report_problem</i>
          </div>
        )}
    </div>
  )
}
// SymbolDetail Body内 Header部分(SP用)
const SpHeader = () => {
  const symbolDetailModalContext = useContext(SymbolDetailModalContext)
  const { symbolDetail, onHide, symbolDetailState } = symbolDetailModalContext

  const { symbol, symbolName } = useMemo(() => {
    const symbol = get(symbolDetail, "Symbol")
    const symbolName = get(symbol, "SymbolNameS") || get(symbol, "SymbolName")
    return {
      symbol,
      symbolName,
    }
  }, [symbolDetail])
  const [favoriteShow, displayFavorite] = useState<boolean>(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)

  return (
    <>
      <div className="symbol-name-group">
        <div className="btn btn-primary">{Formatter.symbol(symbol)}</div>
        <div className="modal-header-symbol-name">{symbolName}</div>
      </div>
      <div className="icon-group">
        <div
          className="header-icon ripple"
          onClick={() => {
            store.dispatch(initRegisterLoad())
            displayFavorite(!favoriteShow)
          }}
        >
          <i className="material-icons">star</i>
        </div>
        <div
          className="header-icon ripple"
          onClick={() => {
            store.dispatch(
              showSymbol(
                get(symbolDetail, "Symbol"),
                symbolDetailState.currentTab,
                true
              )
            )
            store.dispatch(refreshSymbol())
          }}
        >
          <i className="material-icons">sync</i>
        </div>
        <div
          className={ClassNames("header-icon ripple tooltip-relative", {
            active: tooltipVisible,
          })}
          onClick={() => {
            setTooltipVisible(!tooltipVisible)
          }}
        >
          <i className="material-icons icon-ripple">info</i>
        </div>
        {tooltipVisible && (
          <SpHeaderDetailTooltip closeFunc={() => setTooltipVisible(false)} />
        )}
        <div className="header-icon ripple" onClick={() => onHide()}>
          <i className="material-icons">close</i>
        </div>
      </div>
      <FavoriteSymbol
        show={favoriteShow}
        onHide={() => displayFavorite(false)}
        Symbol={symbol}
      />
    </>
  )
}
// SymbolDetail SP用モーダル(HeaderBtns + CalcInvestmentCost)
const SpHeaderDetailTooltip = (props: { closeFunc: Function }) => {
  const symbolDetailModalContext = useContext(SymbolDetailModalContext)
  const { symbolDetail } = symbolDetailModalContext
  const [calcShow, displayCalc] = useState<boolean>(false)

  const symbol = useMemo(() => {
    return get(symbolDetail, "Symbol")
  }, [symbolDetail])

  return (
    <>
      <div className="tooltip-wrapper">
        <div className="tooltip-contents">
          <div className="symbol-detail-buttons">
            <HeaderBtns />
          </div>
          <div className="calc-investment-cost">
            <div
              className="btn btn-primary btn-shaped calc-investment-cost-btn"
              onClick={() => {
                displayCalc(true)
                // props.closeFunc()
              }}
            >
              投資金額計算
            </div>
          </div>
        </div>
      </div>
      <div
        className="close-trigger"
        onClick={() => {
          !calcShow ? props.closeFunc() : displayCalc(false)
        }}
      />
      <CalcInvestmentCost
        show={calcShow}
        onHide={() => displayCalc(false)}
        Symbol={symbol}
      />
    </>
  )
}
